/** Ricerca luoghi e reverse geocoding con Nominatim (OpenStreetMap, gratuito). */

import type { LatLng } from './geo';

const BASE = 'https://nominatim.openstreetmap.org';

export interface GeocodeResult {
  ll: LatLng;
  label: string;
}

interface NominatimItem {
  lat: string;
  lon: string;
  display_name?: string;
  name?: string;
}

function shortLabel(displayName: string | undefined, fallback: string): string {
  if (!displayName) return fallback;
  const parts = displayName.split(',').map((p) => p.trim());
  return parts.slice(0, 3).join(', ');
}

export async function searchPlaces(
  query: string,
  opts: { limit?: number; near?: LatLng; signal?: AbortSignal; fetchImpl?: typeof fetch } = {},
): Promise<GeocodeResult[]> {
  const fetchImpl = opts.fetchImpl ?? fetch;
  const params = new URLSearchParams({
    format: 'jsonv2',
    q: query,
    limit: String(opts.limit ?? 5),
    'accept-language': 'it',
  });
  if (opts.near) {
    const d = 0.35;
    params.set(
      'viewbox',
      [opts.near.lng - d, opts.near.lat + d, opts.near.lng + d, opts.near.lat - d].join(','),
    );
    // bounded=0: il viewbox è solo una preferenza, non un filtro
    params.set('bounded', '0');
  }
  const res = await fetchImpl(`${BASE}/search?${params.toString()}`, {
    signal: opts.signal ?? null,
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Nominatim HTTP ${res.status}`);
  const items = (await res.json()) as NominatimItem[];
  return items
    .map((it) => {
      const lat = parseFloat(it.lat);
      const lng = parseFloat(it.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { ll: { lat, lng }, label: shortLabel(it.display_name, `${it.lat}, ${it.lon}`) };
    })
    .filter((r): r is GeocodeResult => r !== null);
}

export async function reverseLabel(
  ll: LatLng,
  opts: { signal?: AbortSignal; fetchImpl?: typeof fetch } = {},
): Promise<string | null> {
  const fetchImpl = opts.fetchImpl ?? fetch;
  const params = new URLSearchParams({
    format: 'jsonv2',
    lat: String(ll.lat),
    lon: String(ll.lng),
    zoom: '17',
    'accept-language': 'it',
  });
  try {
    const res = await fetchImpl(`${BASE}/reverse?${params.toString()}`, {
      signal: opts.signal ?? null,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const item = (await res.json()) as NominatimItem & { error?: string };
    if (item.error) return null;
    return shortLabel(item.display_name, item.name ?? '');
  } catch {
    return null;
  }
}
